import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
	ArrowDownLeft,
	ArrowUpRight,
	Banknote,
	CalendarClock,
	CheckCircle2,
	Percent,
	RefreshCw,
	TrendingUp,
	UserCheck,
	Wallet,
} from 'lucide-react'
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'
import {
	fetchAccounts,
	fetchBackOfficeMetrics,
	fetchBoletoDueDateSummary,
	fetchFloatRevenue,
	fetchOnboardings,
	fetchPayingAgents,
} from '../services/api'
import { Alert, Button, Card, DetailGrid, DetailItem, PageHeader, Select, TextInput, formatCurrency } from '../components/ui'

const formatShortDate = (isoDate) => {
	const [, month, day] = isoDate.split('-')
	return `${day}/${month}`
}

const currentMonth = () => new Date().toISOString().slice(0, 7)

const CARDS = [
	{
		key: 'accounts',
		name: 'Contas',
		href: '/contas',
		icon: Wallet,
		load: () => fetchAccounts({ page: 1, perPage: 1 }),
	},
	{
		key: 'onboardings',
		name: 'Onboardings',
		href: '/onboardings',
		icon: UserCheck,
		load: () => fetchOnboardings({ page: 1, perPage: 1 }),
	},
	{
		key: 'payingAgents',
		name: 'Pagadorias',
		href: '/pagadoria',
		icon: Banknote,
		load: () => fetchPayingAgents({ page: 1, perPage: 1 }),
	},
]

const Dashboard = () => {
	const [totals, setTotals] = useState({})
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')

	const [accountOptions, setAccountOptions] = useState([])
	const [accountId, setAccountId] = useState('')
	const [month, setMonth] = useState(currentMonth)
	const [metrics, setMetrics] = useState(null)
	const [isLoadingMetrics, setIsLoadingMetrics] = useState(true)
	const [metricsError, setMetricsError] = useState('')

	const [floatRevenue, setFloatRevenue] = useState(null)
	const [isLoadingFloatRevenue, setIsLoadingFloatRevenue] = useState(true)
	const [floatRevenueError, setFloatRevenueError] = useState('')

	const [boletoDueDateSummary, setBoletoDueDateSummary] = useState(null)
	const [isLoadingBoletoDueDateSummary, setIsLoadingBoletoDueDateSummary] = useState(true)
	const [boletoDueDateSummaryError, setBoletoDueDateSummaryError] = useState('')

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')

		const results = await Promise.allSettled(CARDS.map((card) => card.load()))
		const nextTotals = {}
		const failures = []

		results.forEach((result, index) => {
			const card = CARDS[index]
			if (result.status === 'fulfilled') {
				nextTotals[card.key] = result.value?.totalElements ?? 0
			} else {
				nextTotals[card.key] = null
				failures.push(card.name)
			}
		})

		setTotals(nextTotals)
		if (failures.length > 0) {
			setError(`Nao foi possivel carregar: ${failures.join(', ')}`)
		}
		setIsLoading(false)
	}, [])

	// Carrega ate 200 contas para popular o filtro - suficiente para o volume
	// atual do backoffice, sem precisar de um combobox de busca.
	const loadAccountOptions = useCallback(async () => {
		try {
			const result = await fetchAccounts({ page: 1, perPage: 200 })
			const content = result?.content ?? []
			content.sort((a, b) => (a.accountHolder?.name || '').localeCompare(b.accountHolder?.name || ''))
			setAccountOptions(content)
		} catch {
			setAccountOptions([])
		}
	}, [])

	const loadMetrics = useCallback(async (selectedAccountId, selectedMonth) => {
		setIsLoadingMetrics(true)
		setMetricsError('')
		try {
			const result = await fetchBackOfficeMetrics({
				...(selectedAccountId ? { accountPublicId: selectedAccountId } : {}),
				month: selectedMonth,
			})
			setMetrics(result)
		} catch (err) {
			setMetrics(null)
			setMetricsError(err.message)
		} finally {
			setIsLoadingMetrics(false)
		}
	}, [])

	// Receita float independe do filtro de conta (e sempre a soma de todas as
	// contas da plataforma) e depende de uma fonte externa (CDI do BCB) - fica
	// em loading/erro proprios para nao travar o resto do dashboard se falhar.
	const loadFloatRevenue = useCallback(async (selectedMonth) => {
		setIsLoadingFloatRevenue(true)
		setFloatRevenueError('')
		try {
			const result = await fetchFloatRevenue({ month: selectedMonth })
			setFloatRevenue(result)
		} catch (err) {
			setFloatRevenue(null)
			setFloatRevenueError(err.message)
		} finally {
			setIsLoadingFloatRevenue(false)
		}
	}, [])

	// Boletos a vencer por dia - mesmo criterio do card "Boletos a vencer",
	// quebrado por dia de vencimento. Respeita o mesmo filtro de conta/mes das
	// Metricas de negocio.
	const loadBoletoDueDateSummary = useCallback(async (selectedAccountId, selectedMonth) => {
		setIsLoadingBoletoDueDateSummary(true)
		setBoletoDueDateSummaryError('')
		try {
			const result = await fetchBoletoDueDateSummary({
				...(selectedAccountId ? { accountPublicId: selectedAccountId } : {}),
				month: selectedMonth,
			})
			setBoletoDueDateSummary(result)
		} catch (err) {
			setBoletoDueDateSummary(null)
			setBoletoDueDateSummaryError(err.message)
		} finally {
			setIsLoadingBoletoDueDateSummary(false)
		}
	}, [])

	useEffect(() => {
		load()
		loadAccountOptions()
	}, [load, loadAccountOptions])

	useEffect(() => {
		loadMetrics(accountId, month)
	}, [accountId, month, loadMetrics])

	useEffect(() => {
		loadFloatRevenue(month)
	}, [month, loadFloatRevenue])

	useEffect(() => {
		loadBoletoDueDateSummary(accountId, month)
	}, [accountId, month, loadBoletoDueDateSummary])

	const refreshAll = () => {
		load()
		loadAccountOptions()
		loadMetrics(accountId, month)
		loadFloatRevenue(month)
		loadBoletoDueDateSummary(accountId, month)
	}

	// Cada card mostra a quantidade da operacao no mes como numero principal e,
	// quando aplicavel, a tarifa ja coletada para essa operacao como linha secundaria.
	const metricCards = metrics
		? [
				{
					key: 'boletosAVencer',
					name: 'Boletos a vencer',
					icon: CalendarClock,
					primary: `${metrics.boletosAVencer.count}`,
				},
				{
					key: 'boletosLiquidados',
					name: 'Boletos liquidados',
					icon: CheckCircle2,
					primary: `${metrics.boletosLiquidados.count}`,
					sub: `${formatCurrency(metrics.boletosLiquidados.feeAmount)} em tarifa`,
				},
				{
					key: 'pixEnviados',
					name: 'Pix enviados',
					icon: ArrowUpRight,
					primary: `${metrics.pixEnviados.count}`,
					sub: `${formatCurrency(metrics.pixEnviados.feeAmount)} em tarifa`,
				},
				{
					key: 'pixRecebidos',
					name: 'Pix recebidos',
					icon: ArrowDownLeft,
					primary: `${metrics.pixRecebidos.count}`,
					sub: `${formatCurrency(metrics.pixRecebidos.feeAmount)} em tarifa`,
				},
				{
					key: 'taxas',
					name: 'Tarifas cobradas',
					icon: Percent,
					primary: formatCurrency(metrics.taxas.amount),
					sub: `${metrics.taxas.count} cobranca(s)`,
				},
				{
					key: 'saldo',
					name: 'Saldo total',
					icon: Wallet,
					primary: formatCurrency(metrics.saldoTotal),
					sub: 'saldo somado das contas ao final do mes selecionado',
				},
			]
		: []

	const floatDays = (floatRevenue?.days ?? []).map((day) => ({
		...day,
		label: formatShortDate(day.date),
	}))
	let floatAccumulated = 0
	const floatChartData = floatDays.map((day) => {
		floatAccumulated += day.netRevenue
		return { ...day, accumulated: floatAccumulated }
	})

	const boletoChartData = (boletoDueDateSummary?.days ?? []).map((day) => ({
		...day,
		label: formatShortDate(day.date),
	}))
	const boletoTotals = boletoChartData.reduce(
		(acc, day) => ({ count: acc.count + day.count, totalAmount: acc.totalAmount + day.totalAmount }),
		{ count: 0, totalAmount: 0 },
	)

	return (
		<div>
			<PageHeader
				title="Dashboard"
				subtitle="Visao geral do backoffice do KSI Pay"
				actions={
					<Button variant="secondary" onClick={refreshAll} disabled={isLoading || isLoadingMetrics}>
						<RefreshCw className="h-4 w-4" />
						Atualizar
					</Button>
				}
			/>

			<Alert onDismiss={() => setError('')}>{error}</Alert>

			<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
				{CARDS.map((card) => (
					<Link
						key={card.key}
						to={card.href}
						className="rounded-lg bg-white p-5 shadow transition hover:shadow-md"
					>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm font-medium text-gray-500">{card.name}</p>
								<p className="mt-2 text-3xl font-bold text-gray-900">
									{isLoading ? '...' : (totals[card.key] ?? '—')}
								</p>
							</div>
							<span className="rounded-lg bg-primary-50 p-3">
								<card.icon className="h-6 w-6 text-primary-600" />
							</span>
						</div>
						<p className="mt-4 text-sm text-primary-600">Ver todos →</p>
					</Link>
				))}
			</div>

			<div className="mt-8">
				<PageHeader
					title="Metricas de negocio"
					subtitle="Quantidade de operacoes no mes e tarifa ja coletada - filtre por conta e por mes"
					actions={
						<div className="flex flex-wrap items-end gap-2">
							<TextInput
								id="metrics-month-filter"
								type="month"
								value={month}
								onChange={(event) => setMonth(event.target.value || currentMonth())}
								className="w-40"
							/>
							<Select
								id="metrics-account-filter"
								value={accountId}
								onChange={(event) => setAccountId(event.target.value)}
								className="w-72"
								options={[
									{ value: '', label: 'Todas as contas' },
									...accountOptions.map((account) => ({
										value: account.id,
										label: `${account.number ?? '—'} · ${account.accountHolder?.name ?? 'Sem titular'}`,
									})),
								]}
							/>
						</div>
					}
				/>

				<Alert onDismiss={() => setMetricsError('')}>{metricsError}</Alert>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{(isLoadingMetrics && !metrics ? Array(6).fill(null) : metricCards).map((card, index) => (
						<div key={card?.key ?? index} className="rounded-lg bg-white p-4 shadow">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-sm font-medium text-gray-500">{card?.name ?? '...'}</p>
									<p className="mt-1 text-2xl font-bold text-gray-900">
										{isLoadingMetrics || !card ? '...' : card.primary}
									</p>
									{card?.sub && !isLoadingMetrics && (
										<p className="mt-1 text-xs text-gray-400">{card.sub}</p>
									)}
								</div>
								{card && (
									<span className="rounded-lg bg-primary-50 p-3">
										<card.icon className="h-6 w-6 text-primary-600" />
									</span>
								)}
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="mt-8">
				<PageHeader
					title="Receita float"
					subtitle="Rebate diario sobre o saldo total das contas, com o CDI diario (BCB) - sempre a soma de todas as contas da plataforma, ja liquido de impostos"
					actions={
						<TextInput
							id="float-revenue-month-filter"
							type="month"
							value={month}
							onChange={(event) => setMonth(event.target.value || currentMonth())}
							className="w-40"
						/>
					}
				/>

				<Alert onDismiss={() => setFloatRevenueError('')}>{floatRevenueError}</Alert>

				<div className="rounded-lg bg-white p-4 shadow">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-gray-500">Receita float acumulada no mes (liquida)</p>
							<p className="mt-1 text-2xl font-bold text-gray-900">
								{isLoadingFloatRevenue ? '...' : formatCurrency(floatRevenue?.accumulated?.netRevenue ?? 0)}
							</p>
							{!isLoadingFloatRevenue && floatRevenue && (
								<p className="mt-1 text-xs text-gray-400">
									bruto {formatCurrency(floatRevenue.accumulated.grossRevenue)} · impostos{' '}
									{formatCurrency(floatRevenue.accumulated.taxAmount)} · considera dias ja fechados do mes
								</p>
							)}
						</div>
						<span className="rounded-lg bg-primary-50 p-3">
							<TrendingUp className="h-6 w-6 text-primary-600" />
						</span>
					</div>
				</div>

				<Card
					title="Receita float diaria"
					description="Receita liquida do dia, acumulado do mes e saldo total das contas usado no calculo (eixo direito)"
					className="mt-4"
				>
					{isLoadingFloatRevenue ? (
						<p className="text-sm text-gray-500">Carregando...</p>
					) : floatChartData.length === 0 ? (
						<p className="text-sm text-gray-500">Nenhum dia fechado neste mes ainda.</p>
					) : (
						<div className="h-72 w-full">
							<ResponsiveContainer width="100%" height="100%">
								<LineChart data={floatChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
									<CartesianGrid strokeDasharray="3 3" vertical={false} />
									<XAxis dataKey="label" tick={{ fontSize: 12 }} />
									<YAxis
										yAxisId="revenue"
										tick={{ fontSize: 12 }}
										tickFormatter={(value) => formatCurrency(value)}
										width={90}
									/>
									<YAxis
										yAxisId="balance"
										orientation="right"
										tick={{ fontSize: 12 }}
										tickFormatter={(value) => formatCurrency(value)}
										width={90}
									/>
									<Tooltip
										formatter={(value, name) => [formatCurrency(value), name]}
										labelFormatter={(label) => `Dia ${label}`}
									/>
									<Legend />
									<Line
										yAxisId="revenue"
										type="monotone"
										dataKey="netRevenue"
										name="Receita do dia"
										stroke="#2563eb"
										strokeWidth={2}
										dot={false}
									/>
									<Line
										yAxisId="revenue"
										type="monotone"
										dataKey="accumulated"
										name="Acumulado"
										stroke="#16a34a"
										strokeWidth={2}
										dot={false}
									/>
									<Line
										yAxisId="balance"
										type="monotone"
										dataKey="totalBalance"
										name="Saldo do dia"
										stroke="#f59e0b"
										strokeWidth={2}
										strokeDasharray="4 3"
										dot={false}
									/>
								</LineChart>
							</ResponsiveContainer>
						</div>
					)}
				</Card>
			</div>

			<div className="mt-8">
				<PageHeader
					title="Boletos por vencimento"
					subtitle="Boletos a vencer no mes, por dia de vencimento - filtre por conta e por mes"
					actions={
						<div className="flex flex-wrap items-end gap-2">
							<TextInput
								id="boletos-vencimento-month-filter"
								type="month"
								value={month}
								onChange={(event) => setMonth(event.target.value || currentMonth())}
								className="w-40"
							/>
							<Select
								id="boletos-vencimento-account-filter"
								value={accountId}
								onChange={(event) => setAccountId(event.target.value)}
								className="w-72"
								options={[
									{ value: '', label: 'Todas as contas' },
									...accountOptions.map((account) => ({
										value: account.id,
										label: `${account.number ?? '—'} · ${account.accountHolder?.name ?? 'Sem titular'}`,
									})),
								]}
							/>
						</div>
					}
				/>

				<Alert onDismiss={() => setBoletoDueDateSummaryError('')}>{boletoDueDateSummaryError}</Alert>

				<div className="rounded-lg bg-white p-4 shadow">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-gray-500">Boletos a vencer no mes</p>
							<p className="mt-1 text-2xl font-bold text-gray-900">
								{isLoadingBoletoDueDateSummary ? '...' : `${boletoTotals.count} boleto(s)`}
							</p>
							{!isLoadingBoletoDueDateSummary && boletoDueDateSummary && (
								<p className="mt-1 text-xs text-gray-400">
									valor total {formatCurrency(boletoTotals.totalAmount)}
								</p>
							)}
						</div>
						<span className="rounded-lg bg-primary-50 p-3">
							<CalendarClock className="h-6 w-6 text-primary-600" />
						</span>
					</div>
				</div>

				<Card
					title="Boletos a vencer por dia"
					description="Quantidade e valor total dos boletos com vencimento em cada dia do mes (eixo direito)"
					className="mt-4"
				>
					{isLoadingBoletoDueDateSummary ? (
						<p className="text-sm text-gray-500">Carregando...</p>
					) : boletoChartData.length === 0 ? (
						<p className="text-sm text-gray-500">Nenhum boleto a vencer neste mes.</p>
					) : (
						<div className="h-72 w-full">
							<ResponsiveContainer width="100%" height="100%">
								<LineChart data={boletoChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
									<CartesianGrid strokeDasharray="3 3" vertical={false} />
									<XAxis dataKey="label" tick={{ fontSize: 12 }} />
									<YAxis
										yAxisId="count"
										tick={{ fontSize: 12 }}
										allowDecimals={false}
										width={50}
									/>
									<YAxis
										yAxisId="amount"
										orientation="right"
										tick={{ fontSize: 12 }}
										tickFormatter={(value) => formatCurrency(value)}
										width={90}
									/>
									<Tooltip
										formatter={(value, name) => [
											name === 'Quantidade' ? value : formatCurrency(value),
											name,
										]}
										labelFormatter={(label) => `Dia ${label}`}
									/>
									<Legend />
									<Line
										yAxisId="count"
										type="monotone"
										dataKey="count"
										name="Quantidade"
										stroke="#2563eb"
										strokeWidth={2}
										dot={false}
									/>
									<Line
										yAxisId="amount"
										type="monotone"
										dataKey="totalAmount"
										name="Valor total"
										stroke="#f59e0b"
										strokeWidth={2}
										strokeDasharray="4 3"
										dot={false}
									/>
								</LineChart>
							</ResponsiveContainer>
						</div>
					)}
				</Card>
			</div>

			{metrics && (
				<Card
					title="Contas"
					description="Distribuicao das contas consideradas no filtro acima"
					className="mt-8"
				>
					<DetailGrid cols={4}>
						<DetailItem label="Total">{metrics.accounts.total}</DetailItem>
						<DetailItem label="Ativas">{metrics.accounts.active}</DetailItem>
						<DetailItem label="Pendentes">{metrics.accounts.pending}</DetailItem>
						<DetailItem label="Reprovadas">{metrics.accounts.reproved}</DetailItem>
						<DetailItem label="Bloqueadas">{metrics.accounts.locked}</DetailItem>
						<DetailItem label="Encerradas">{metrics.accounts.closed}</DetailItem>
						<DetailItem label="Pessoa fisica">{metrics.accounts.pf}</DetailItem>
						<DetailItem label="Pessoa juridica">{metrics.accounts.pj}</DetailItem>
					</DetailGrid>
				</Card>
			)}

			<Card title="Sobre este painel" className="mt-6">
				<p className="text-sm text-gray-600">
					As telas deste painel espelham a pasta <strong>BackOffice</strong> da collection{' '}
					<code className="font-mono text-xs">API.postman_collection.json</code>: Contas,
					Onboardings, Pagadoria e Utils. Use o menu lateral para navegar entre os modulos.
				</p>
			</Card>
		</div>
	)
}

export default Dashboard
